import React from 'react';
import { Box } from '@mui/material';

interface MarkdownRendererProps {
  content: string;
  sources?: any[];
  projectDocuments?: any[];
  onSourceClick?: (source: any) => void;
  messageId?: number;
}

// Helper function to process citations in table cells
const processTableCitation = (citationText: string, sources: any[], hasBrackets: boolean): string => {
  const pageMatch = citationText.match(/Pages?\s+(\d+)/i);
  const citationPage = pageMatch ? parseInt(pageMatch[1]) : null;
  
  if (citationPage && sources.length > 0) {
    // Find source by page number
    const matchedSource = sources.find(s => 
      s.page === citationPage || s.metadata?.page === citationPage
    );
    
    if (matchedSource) {
      const sourceId = `CITATION_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      (window as any).citationSources = (window as any).citationSources || {};
      (window as any).citationSources[sourceId] = { 
        source: matchedSource, 
        text: citationText,
        page: citationPage
      };
      
      // Return clickable citation
      const displayText = hasBrackets ? `[${citationText}]` : citationText;
      return `<span data-citation="${sourceId}" data-page="${citationPage}">${displayText}</span>`;
    }
  }
  
  // Return original if no match found
  return hasBrackets ? `[${citationText}]` : citationText;
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  sources = [],
  projectDocuments = [],
  onSourceClick,
  messageId
}) => {
  // Process message content to handle inline citations BEFORE markdown processing
  const processMessageWithCitations = (message: string, sources?: any[]) => {
    let processed = message;
    

    
    // CRITICAL: Clear previous citation storage to prevent cross-contamination
    (window as any).citationSources = {};
    
    // Remove HTML attributes that might be showing as text
    processed = processed.replace(/onmouseover="[^"]*"/gi, '');
    processed = processed.replace(/onmouseout="[^"]*"/gi, '');
    processed = processed.replace(/style="[^"]*"/gi, '');
    processed = processed.replace(/class="[^"]*"/gi, '');
    processed = processed.replace(/id="[^"]*"/gi, '');
    
    // Remove any remaining HTML-like attributes
    processed = processed.replace(/\s*(onmouseover|onmouseout|style|class|id)="[^"]*"/gi, '');
    
    // Remove stray HTML content
    processed = processed.replace(/\s*onmouseover[^>]*>/gi, '');
    processed = processed.replace(/\s*onmouseout[^>]*>/gi, '');
    
    // Clean up malformed HTML tags
    processed = processed.replace(/<[^>]*onmouseover[^>]*>/gi, '');
    processed = processed.replace(/<[^>]*onmouseout[^>]*>/gi, '');
    
    // Process citations BEFORE markdown rendering
    if (sources && sources.length > 0) {
      // Pattern to match citations like [Information, Page 3] or [Document Name, Pages 1, 2, 3]
      // Also handles citations at end of sentences with period after bracket
      const citationPattern = /\[([^\]]+)\]\.?/g;
      
      processed = processed.replace(citationPattern, (match, citationText) => {
        // Check if this is a multi-page citation (Pages 1, 2, 3, etc.)
        const multiPageMatch = citationText.match(/Pages?\s+([\d,\s]+)/i);
        
        if (multiPageMatch) {
          // Extract document name (everything before ", Page" or ", Pages")
          const docNameMatch = citationText.match(/^([^,]+),\s*Pages?/i);
          const docName = docNameMatch ? docNameMatch[1].trim() : '';
          
          // Extract all page numbers - handle both comma-separated and space-separated
          const pageNumbersStr = multiPageMatch[1];
          const pageNumbers = pageNumbersStr
            .split(/[,\s]+/)  // Split by comma or space
            .map((p: string) => parseInt(p.trim()))
            .filter((p: number) => !isNaN(p));
          
          //console.log(`📚 Multi-page citation found:`, { docName, pageNumbers, originalText: citationText });
          
          // Create clickable links for each page
          const pageLinks = pageNumbers.map((pageNum: number, idx: number) => {
            // Find source for this specific page
            const matchedSource = sources.find(source => 
              source.page === pageNum || source.metadata?.page === pageNum
            );
            
            if (matchedSource) {
              const sourceId = `CITATION_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`;
              (window as any).citationSources = (window as any).citationSources || {};
              (window as any).citationSources[sourceId] = {
                source: matchedSource,
                text: `${docName}, Page ${pageNum}`,
                page: pageNum
              };
              return `<span data-citation="${sourceId}" data-page="${pageNum}">${pageNum}</span>`;
            } else {
              // No source found for this page, return plain text
              //console.log(`⚠️ No source found for page ${pageNum}`);
              return `${pageNum}`;
            }
          }).join(', ');
          
          // Return formatted citation with clickable page numbers
          const result = `[${docName}, Page${pageNumbers.length > 1 ? 's' : ''} ${pageLinks}]`;
          //console.log(`✅ Multi-page citation processed:`, result.substring(0, 100));
          return result;
        } else {
          // Single page citation - existing logic
          const pageMatch = citationText.match(/Page\s+(\d+)/i);
          const citationPage = pageMatch ? parseInt(pageMatch[1]) : null;
          
          // Match citation by page number - check both metadata.page and direct page property
          let matchedSource: any = null;
          
          if (citationPage && sources.length > 0) {
            // Citation objects have page directly, not in metadata
            matchedSource = sources.find(source => 
              source.page === citationPage || source.metadata?.page === citationPage
            );
            
            console.log(`🔗 Citation "${citationText}" -> Page ${citationPage} -> Source found:`, !!matchedSource);
            if (matchedSource) {
              console.log(`✅ Matched source:`, { page: matchedSource.page, metadataPage: matchedSource.metadata?.page });
            }
          }
          
          // If no page match, try to find by text similarity
          if (!matchedSource && sources.length > 0) {
            matchedSource = sources.find(source => source.text === citationText);
            if (matchedSource) {
              console.log(`✅ Matched by text for "${citationText}"`);
            }
          }
          
          // Last resort: use first source
          if (!matchedSource && sources.length > 0) {
            matchedSource = sources[0];
           // console.log(`⚠️ Using first source as fallback for "${citationText}"`);
          }

          // Create a unique placeholder that won't be processed by markdown
          if (matchedSource) {
            const sourceId = `CITATION_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            // Store the source data globally for later retrieval
            (window as any).citationSources = (window as any).citationSources || {};
            (window as any).citationSources[sourceId] = {
              source: matchedSource,
              text: citationText
            };
            
            // Return a placeholder that markdown won't process
            return `<span data-citation="${sourceId}">[${citationText}]</span>`;
          }
          
          return match; // Return original if no source found
        }
      });
    }
    
    return processed;
  };

  const processedContent = processMessageWithCitations(content, sources);

  return (
    <Box sx={{ 
      '& > *:first-of-type': { marginTop: 0 },
      '& > *:last-child': { marginBottom: 0 }
    }}>
      <Box
        dangerouslySetInnerHTML={{
          __html: (() => {
            // Convert markdown to HTML using a simple approach like nexus-fe
            let htmlContent = processedContent;
            
            // Process tables first (before other replacements)
            htmlContent = htmlContent.replace(/(\|[^\n]*\|[\n\r]*)+/g, (tableMatch) => {
              const lines = tableMatch.trim().split(/[\n\r]+/);
              if (lines.length < 2) return tableMatch;
              
              let tableHtml = '<div style="margin: 1rem 0; overflow-x: auto;"><table style="width: 100%; border-collapse: collapse; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1); border: 1px solid rgba(0, 0, 0, 0.12);">';
              
              lines.forEach((line, index) => {
                const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
                
                // Skip separator lines (lines with only dashes and pipes)
                if (line.match(/^[\s|\-+=]*$/)) return;
                
                if (cells.length > 0) {
                  const isHeader = index === 0 || (index === 1 && lines[0].match(/^[\s|\-+=]*$/));
                  const tag = isHeader ? 'th' : 'td';
                  const style = isHeader 
                    ? 'padding: 12px 16px; border-bottom: 2px solid rgba(0, 0, 0, 0.12); font-weight: 600; color: #1976d2; background: rgba(25, 118, 210, 0.08); text-align: left; font-size: 0.875rem;'
                    : 'padding: 12px 16px; border-bottom: 1px solid rgba(0, 0, 0, 0.12); font-size: 0.875rem;';
                  
                  tableHtml += '<tr>';
                  cells.forEach(cell => {
                    // Process citations in table cells - handle both bracketed and non-bracketed citations
                    let cellContent = cell;
                    
                    // Pattern 1: Citations with brackets [Doc, Page X]
                    const bracketedPattern = /\[([^\]]+,\s*Pages?\s+[\d,\s]+)\]/g;
                    cellContent = cellContent.replace(bracketedPattern, (match, citationText) => {
                      return processTableCitation(citationText, sources, true);
                    });
                    
                    // Pattern 2: Citations without brackets (common in tables): "Doc - CIM.pdf, Page X"
                    const unbracketedPattern = /([A-Za-z\s]+ - CIM\.pdf, Pages?\s+[\d,\s]+)/g;
                    cellContent = cellContent.replace(unbracketedPattern, (match, citationText) => {
                      return processTableCitation(citationText, sources, false);
                    });
                    
                    tableHtml += `<${tag} style="${style}">${cellContent}</${tag}>`;
                  });
                  tableHtml += '</tr>';
                }
              });
              
              tableHtml += '</table></div>';
              return tableHtml;
            });
            
            // Process math formulas BEFORE other markdown elements
            // Block math: \[ ... \]
            htmlContent = htmlContent.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
              return `<div style="margin: 1rem 0; padding: 1rem; background-color: rgba(25, 118, 210, 0.05); border-left: 3px solid #1976d2; border-radius: 4px; overflow-x: auto; font-family: 'Courier New', monospace; font-size: 0.95rem;">${formula.trim()}</div>`;
            });
            
            // Inline math: \( ... \)
            htmlContent = htmlContent.replace(/\\\(([\s\S]*?)\\\)/g, (match, formula) => {
              return `<span style="background-color: rgba(25, 118, 210, 0.08); padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace; font-size: 0.9rem;">${formula.trim()}</span>`;
            });
            
            // Then process other markdown elements
            htmlContent = htmlContent
              // Headers (process from most specific to least specific)
              .replace(/(^|\n)#### (.*?)(?=\n|$)/gim, '$1<h4 style="font-size: 1.1rem; font-weight: 600; margin: 1rem 0 0.5rem 0;">$2</h4>')
              .replace(/(^|\n)### (.*?)(?=\n|$)/gim, '$1<h3 style="font-size: 1.25rem; font-weight: 600; margin: 1.25rem 0 0.5rem 0;">$2</h3>')
              .replace(/(^|\n)## (.*?)(?=\n|$)/gim, '$1<h2 style="font-size: 1.5rem; font-weight: 600; margin: 1.5rem 0 0.75rem 0; border-bottom: 1px solid currentColor; opacity: 0.3; padding-bottom: 0.25rem;">$2</h2>')
              .replace(/(^|\n)# (.*?)(?=\n|$)/gim, '$1<h1 style="font-size: 1.75rem; font-weight: 600; margin: 1.5rem 0 0.75rem 0; border-bottom: 2px solid currentColor; opacity: 0.3; padding-bottom: 0.5rem;">$2</h1>')
              // Bold and italic
              .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
              .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
              // Lists
              .replace(/^\* (.*$)/gim, '<li style="margin: 0.25rem 0; line-height: 1.6;">$1</li>')
              .replace(/^- (.*$)/gim, '<li style="margin: 0.25rem 0; line-height: 1.6;">$1</li>')
              .replace(/^\d+\. (.*$)/gim, '<li style="margin: 0.25rem 0; line-height: 1.6; list-style-type: decimal;">$1</li>')
              // Line breaks
              .replace(/\n\n/g, '</p><p style="margin: 0.5rem 0; line-height: 1.6;">')
              .replace(/\n/g, '<br>');
            
            // Wrap in paragraph if needed
            if (!htmlContent.includes('<p>') && !htmlContent.includes('<h1>') && !htmlContent.includes('<h2>') && !htmlContent.includes('<h3>')) {
              htmlContent = `<p style="margin: 0.5rem 0; line-height: 1.6;">${htmlContent}</p>`;
            }
            
            // Wrap consecutive list items in ul/ol tags
            htmlContent = htmlContent.replace(/(<li[^>]*>.*?<\/li>(?:\s*<br>\s*)*)+/g, (match) => {
              const cleanMatch = match.replace(/<br\s*\/?>/g, '');
              if (match.includes('list-style-type: decimal')) {
                return `<ol style="margin: 0.5rem 0; padding-left: 1.5rem; list-style-type: decimal;">${cleanMatch}</ol>`;
              } else {
                return `<ul style="margin: 0.5rem 0; padding-left: 1.5rem; list-style-type: disc;">${cleanMatch}</ul>`;
              }
            });
            
            // Now process the citation placeholders - handle both with and without data-page attribute
            // This regex matches: <span data-citation="ID" data-page="24">24</span> OR <span data-citation="ID">[Full Citation]</span>
            htmlContent = htmlContent.replace(/<span data-citation="([^"]+)"(?:\s+data-page="([^"]*)")?>([^<]+)<\/span>/g, (match, sourceId, dataPage, citationText) => {
              const citationData = (window as any).citationSources?.[sourceId];
              if (citationData) {
                // For multi-page citations, use the data-page attribute to find the right source
                const targetPage = dataPage ? parseInt(dataPage) : citationData.page;
                
                // Find the source index in the sources array
                const sourceIndex = sources.findIndex(s => {
                  const sourcePage = s.page || s.metadata?.page;
                  const sourceDocId = s.metadata?.document_id;
                  const citationDocId = citationData.source?.metadata?.document_id;
                  
                  // Match by page and document ID
                  return sourcePage === targetPage && sourceDocId === citationDocId;
                });
                
                if (sourceIndex !== -1) {
                  // Use window.showSourceModal to trigger the info panel expansion
                  const clickHandler = messageId 
                    ? `window.showSourceModal && window.showSourceModal(${sourceIndex}, ${messageId})`
                    : `window.showSourceModal && window.showSourceModal(${sourceIndex})`;
                  
                  return `<span 
                    onclick="${clickHandler}"
                    style="
                      color: #1976d2;
                      cursor: pointer;
                      text-decoration: underline;
                      font-weight: 500;
                      padding: 1px 2px;
                      border-radius: 3px;
                      transition: all 0.2s ease;
                      background-color: rgba(25, 118, 210, 0.1);
                    "
                    onmouseenter="
                      this.style.backgroundColor = 'rgba(25, 118, 210, 0.2)';
                      this.style.color = '#1565c0';
                    "
                    onmouseleave="
                      this.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
                      this.style.color = '#1976d2';
                    "
                  >${citationText}</span>`;
                } else {
                  console.log(`⚠️ Source not found for citation:`, { sourceId, targetPage, citationText });
                }
              }
              return citationText;
            });
            
            return htmlContent;
          })()
        }}
      />
    </Box>
  );
};

export default MarkdownRenderer;
